import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const EASE = [0.22, 1, 0.36, 1];

const REVEAL = { opacity: 0, y: 30 };
const REVEAL_IN = { opacity: 1, y: 0 };
const VIEWPORT = { once: true, margin: "-80px" };
const TRANS = { duration: 0.8, ease: EASE };

const IMAGES = ["/showroom-1.jpeg", "/showroom-2.jpeg", "/showroom-3.jpeg", "/showroom-4.jpeg"];
const MAP_URL = "https://www.google.com/maps/search/?api=1&query=Mala+Aleja+67+71210+Ilid%C5%BEa+Bosnia";

export default function Showroom() {
  const { t } = useLang();
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setActive((p) => (p + 1) % IMAGES.length), 4000);
    return () => clearInterval(id);
  }, []);

  return (
    <section id="showroom" className="relative overflow-hidden py-28 lg:py-40" data-testid="showroom-section">
      {/* Full-bleed background image slider */}
      <div className="absolute inset-0 z-0">
        {IMAGES.map((src, i) => (
          <img
            key={src}
            src={src}
            alt={t.showroom.captions[i]}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ease-out ${
              i === active ? "opacity-100" : "opacity-0"
            }`}
            data-testid={`showroom-slide-${i}`}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-mobix-navy/90 via-mobix-navy/70 to-mobix-navy/40" />
      </div>

      <div className="relative z-10 max-w-[1800px] mx-auto px-6 sm:px-8 md:px-12 lg:px-20">
        {/* Text content */}
        <motion.div
          className="max-w-xl"
          initial={REVEAL}
          whileInView={REVEAL_IN}
          viewport={VIEWPORT}
          transition={TRANS}
        >
          <p className="text-[13px] font-bold tracking-[0.24em] uppercase text-mobix-orange mb-7">{t.showroom.overline}</p>
          <h2 className="font-extrabold uppercase tracking-tight text-[clamp(2.25rem,6vw,4.5rem)] leading-[1.02] break-words" data-testid="showroom-title">
            {t.showroom.words.map((w, i) => (
              <span key={w} className="block">
                <span className={i === 1 ? "text-mobix-orange" : "text-white"}>{w}</span>
                <span className="text-mobix-orange">.</span>
              </span>
            ))}
          </h2>

          <span className="mt-9 block h-[3px] w-32 bg-mobix-orange" aria-hidden="true" />

          <div className="mt-9 max-w-md space-y-4 text-base sm:text-lg font-medium text-white/85 leading-relaxed">
            <p>{t.showroom.paragraph1}</p>
            <p>{t.showroom.paragraph2}</p>
          </div>

          <a
            href={MAP_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="showroom-map-button"
            className="group mt-12 inline-flex items-center gap-3 rounded-3xl bg-mobix-orange px-9 py-4 text-base font-bold text-white transition-all duration-300 hover:bg-[#d95f22] hover:-translate-y-0.5 shadow-[0_16px_32px_rgba(240,108,40,0.30)]"
          >
            {t.showroom.viewMap}
            <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </motion.div>
      </div>

      {/* progress dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2.5">
        {IMAGES.map((src, i) => (
          <button
            key={src}
            onClick={() => setActive(i)}
            aria-label={t.showroom.captions[i]}
            data-testid={`showroom-dot-${i}`}
            className={`h-2 rounded-full transition-all duration-500 ${
              i === active ? "w-7 bg-mobix-orange" : "w-2 bg-white/60 hover:bg-white"
            }`}
          />
        ))}
      </div>
    </section>
  );
}
